import { EN_STRINGS, FR_STRINGS, FR_CA_STRINGS, AR_STRINGS, ES_STRINGS, ES_MX_STRINGS, DE_STRINGS, CS_STRINGS, FI_STRINGS, HE_STRINGS, IT_STRINGS, JA_STRINGS, KO_STRINGS, NO_STRINGS, PL_STRINGS, PT_STRINGS, PT_PT_STRINGS, SK_STRINGS, SV_STRINGS, TH_STRINGS, ZH_STRINGS, ZH_TW_STRINGS, FA_AF_STRINGS } from './translations';
import type { StringsShape } from './translations';
export type { StringsShape } from './translations';

export const VISITOR_TYPES = {
  GUEST: 'guest',
  CONTRACTOR: 'contractor',
  DELIVERY: 'delivery',
  INTERVIEW: 'interview',
  MEETING: 'meeting',
} as const;

export const ROUTES = {
  HOME: 'home',
  TOUCHLESS: 'touchless',
  PRIVACY_POLICY: 'privacy-policy',
  FULL_NAME: 'full-name',
  VISITOR_TYPES: 'visitortypes',
  SELECT_HOST: 'select-host',
  CAPTURE_PHOTO: 'capturepicture',
  FRONT_BACK_PHOTO: 'frontbackpicture',
  VISITOR_FORM: 'visitorform',
  SIGN_DOCUMENT: 'signdocument',
  VISITOR_PICTURE: 'visitorpicture',
  VISITOR_VIDEO: 'visitor-video',
  CONFIRMATION: 'confirmation',
  PRE_REGISTER: 'preregister',
  PRE_REG_CONFIRMATION: 'pre-reg-confirmation',
  PRE_FILL: 'prefill',
  PRE_FILL_DETAILS: 'prefill-details',
  SIGN_OUT: 'signout',
  SMARTPASS_CHECKIN: 'v/:token',
  SMARTPASS_DOWNLOAD: 'pass/:uuid',
} as const;

export const API_ENDPOINTS = {
  AUTH: '/auth',
  VISITORS: '/visitors',
  TOUCHLESS: '/touchless',
  DOCUMENTS: '/documents',
} as const;

// ── Language infrastructure ───────────────────────────────────────────────────
// LANGUAGE_CODES is the single source of truth for supported language codes.
// Both SUPPORTED_LANGUAGES and TRANSLATIONS are typed against SupportedLanguageCode,
// so adding a code here and omitting it from either map is a TypeScript compile error.
export const LANGUAGE_CODES = ['en', 'fr', 'fr-CA', 'ar', 'es', 'es-MX', 'de', 'cs', 'fi', 'he', 'it', 'ja', 'ko', 'no', 'pl', 'pt', 'pt-PT', 'sk', 'sv', 'th', 'zh', 'zh-TW', 'fa-AF'] as const;
export type SupportedLanguageCode = typeof LANGUAGE_CODES[number];

// Languages written right-to-left — used to set the document's `dir` attribute.
export const RTL_LANGUAGE_CODES: readonly SupportedLanguageCode[] = ['ar', 'he', 'fa-AF'];

export interface LanguageInfo {
  native: string;
  english: string;
}

export const SUPPORTED_LANGUAGES: Record<SupportedLanguageCode, LanguageInfo> = {
  en: { native: 'English',    english: 'English' },
  fr:      { native: 'Français',          english: 'French' },
  'fr-CA': { native: 'Français (Canada)', english: 'French (Canada)' },
  ar: { native: 'العربية',     english: 'Arabic' },
  es: { native: 'Español',    english: 'Spanish' },
  'es-MX': { native: 'Español (México)', english: 'Spanish (Mexico)' },
  de: { native: 'Deutsch',    english: 'German' },
  cs: { native: 'Čeština',    english: 'Czech' },
  fi: { native: 'Suomi',      english: 'Finnish' },
  he: { native: 'עברית',       english: 'Hebrew' },
  it: { native: 'Italiano',   english: 'Italian' },
  ja: { native: '日本語',       english: 'Japanese' },
  ko: { native: '한국어',       english: 'Korean' },
  no: { native: 'Norsk',      english: 'Norwegian' },
  pl: { native: 'Polski',     english: 'Polish' },
  pt:      { native: 'Português (Brasil)',   english: 'Portuguese (Brazil)' },
  'pt-PT': { native: 'Português', english: 'Portuguese' },
  sk: { native: 'Slovenčina', english: 'Slovak' },
  sv: { native: 'Svenska',    english: 'Swedish' },
  th: { native: 'ภาษาไทย',     english: 'Thai' },
  zh:      { native: '中文',         english: 'Chinese' },
  'zh-TW': { native: '中文 (台灣)',  english: 'Chinese (TW)' },
  'fa-AF': { native: 'فارسی',      english: 'Persian' },
};

// ── Translations map — must have an entry for every SupportedLanguageCode ────
export const TRANSLATIONS: Record<SupportedLanguageCode, StringsShape> = {
  en: EN_STRINGS,
  fr: FR_STRINGS,
  'fr-CA': FR_CA_STRINGS,
  ar: AR_STRINGS,
  es: ES_STRINGS,
  'es-MX': ES_MX_STRINGS,
  de: DE_STRINGS,
  cs: CS_STRINGS,
  fi: FI_STRINGS,
  he: HE_STRINGS,
  it: IT_STRINGS,
  ja: JA_STRINGS,
  ko: KO_STRINGS,
  no: NO_STRINGS,
  pl: PL_STRINGS,
  pt: PT_STRINGS,
  'pt-PT': PT_PT_STRINGS,
  sk: SK_STRINGS,
  sv: SV_STRINGS,
  th: TH_STRINGS,
  zh: ZH_STRINGS,
  'zh-TW': ZH_TW_STRINGS,
  'fa-AF': FA_AF_STRINGS,
};

// Backward-compatible alias — existing imports of STRINGS continue to work
export const STRINGS: StringsShape = TRANSLATIONS['en'];
